import { useState } from 'react';
import { BoutonDictee } from '../../components/BoutonDictee.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { lignesDeLAssistant } from '../../lib/assistant-devis';
import { corrigeLaDictee, meilleureDictee } from '../../lib/correction-dictee';
import { remiseLigne } from '../../lib/discounts';
import { trf } from '../../lib/i18n';
import { uid } from '../../lib/utils';

/**
 * L'assistant : on dicte ou on écrit les travaux, et les lignes se remplissent.
 *
 * <p>Il n'existait que dans l'offre. Le patron qui facture directement, sans
 * offre, devait saisir chaque article à la main : la facture a maintenant le
 * même assistant.
 *
 * @param {object} props propriétés
 * @param {'offre'|'facture'} [props.piece] la pièce remplie, pour les textes
 * @param {object} props.form la pièce en cours (lignes, client)
 * @param {Function} props.setForm met la pièce à jour
 * @param {object[]} [props.catalogue] articles de l'entreprise
 * @param {object[]} [props.clients] clients, pour leurs remises
 * @param {boolean} [props.estMag] univers magasin (remises du magasin)
 * @param {string} [props.texte] texte de l'assistant, quand la page le garde
 * @param {(t: string|Function) => void} [props.setTexte] met ce texte à jour
 * @returns {import('react').ReactElement} l'assistant
 */
export function AssistantTravaux({
  piece = 'offre',
  form,
  setForm,
  catalogue,
  clients,
  estMag,
  texte,
  setTexte,
}) {
  const [propre, setPropre] = useState('');
  const valeur = texte ?? propre;
  const change = setTexte || setPropre;

  const remplir = () => {
    if (!valeur.trim()) {
      window.alert('Écrivez ou dictez les travaux d’abord.');
      return;
    }
    const client = (clients || []).find((c) => c.id === form.clientId);
    // Le micro du clavier écrit directement dans la case : l'écriture est
    // corrigée ici aussi, avant de remplir les lignes.
    const nouvelles = lignesDeLAssistant(
      corrigeLaDictee(valeur, catalogue).texte,
      catalogue,
      (a) => (client ? remiseLigne(client, a.id, estMag) || 0 : 0),
      { batiment: !estMag },
    );
    if (!nouvelles.length) {
      window.alert(
        'Aucune prestation reconnue. Emploie des mots du nom de tes articles, séparés par des virgules, ou dicte chaque prestation avec son prix (« 200 francs »).',
      );
      return;
    }
    setForm((p) => ({
      ...p,
      lignes: [...(p.lignes || []), ...nouvelles.map((l) => ({ ...l, id: uid() }))],
    }));
    change('');
    window.alert(
      trf('✓ {0} ligne(s) ajoutée(s). Vérifie les quantités et les prix.', nouvelles.length),
    );
  };

  const facture = piece === 'facture';
  return (
    <div
      style={{
        background: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: 11,
        padding: 12,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 11.5, fontWeight: 800, color: '#3730a3', marginBottom: 3 }}>
        {facture ? '✨ Assistant facture' : '✨ Assistant devis'}
      </div>
      {facture ? (
        <div style={{ fontSize: 10, color: '#6366f1', marginBottom: 8, lineHeight: 1.45 }}>
          Dicte (🎤 du clavier) ou écris les travaux → sépare chaque prestation par une virgule +
          son m². L&apos;assistant remplit les lignes de la facture.
          <br />
          Avec les prix, dicte chaque prestation suivie de son prix : « Déplacement 200 francs,
          dépose carrelage 30 m² fois 50 francs ». Sans m², c&apos;est un forfait.
          <br />
          Tu peux aussi coller des travaux déjà écrits : « 1. Titre », sa description, puis le prix
          (« 46m2 x 12 », « Bloc 500 » ou le montant à la fin).
        </div>
      ) : (
        <div style={{ fontSize: 10, color: '#6366f1', marginBottom: 8, lineHeight: 1.45 }}>
          Dicte (🎤 du clavier) ou écris les travaux → sépare chaque prestation par une virgule +
          son m². L&apos;assistant remplit les lignes (même les articles pas encore dans ton
          catalogue → tu mettras juste le prix).
          <br />
          Avec les prix, dicte chaque prestation suivie de son prix : « Déplacement 200 francs,
          dépose carrelage 30 m² fois 50 francs ». Sans m², c&apos;est un forfait.
          <br />
          Tu peux aussi coller une offre déjà écrite : « 1. Titre », sa description, puis le prix («
          46m2 x 12 », « Bloc 500 » ou le montant à la fin). Touche « Générer » : l&apos;offre est
          faite.
        </div>
      )}
      <textarea
        aria-label={facture ? 'Assistant facture' : 'Assistant devis'}
        value={valeur}
        onChange={(e) => change(e.target.value)}
        rows={valeur.includes('\n') ? 12 : 3}
        placeholder="1 prestation par virgule + son m². Ex : demolition 25, couche accrochage 25, pose carrelage 25"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          border: '1px solid #c7d2fe',
          borderRadius: 9,
          padding: 11,
          // Assez grand pour relire son texte collé ou dicté.
          fontSize: 14,
          lineHeight: 1.45,
          fontFamily: 'inherit',
          resize: 'vertical',
          marginBottom: 8,
        }}
      />
      {/* Chaque dictée est une prestation : elle va à la ligne, sans se coller
          à la précédente (le micro n'écrit pas de virgule). */}
      <BoutonDictee
        onTexte={(t, propositions) => {
          const juste = corrigeLaDictee(
            meilleureDictee(propositions || [t], catalogue) || t,
            catalogue,
          ).texte;
          change((pr) => (pr ? pr.replace(/\s+$/, '') + '\n' : '') + juste);
        }}
      />
      <Btn label="✨ Trouver les articles + remplir" color="#4f46e5" full onClick={remplir} />
    </div>
  );
}
